/**
 * The player's settings, kept on this device (localStorage) and applied without a reload where the
 * engine allows: camera breathing, less motion and vibration at once; graphics quality on the next
 * start (the renderer's buffers, shadows and particles are sized when the scene is built).
 */
export type QualityChoice = 'auto' | 'low' | 'medium' | 'high';
export interface SettingsValues {
  quality: QualityChoice;
  /** The camera's slow idle sway. */
  breath: boolean;
  /** Shake and punch-in at a third, the interface's own motion cut to fades. */
  calm: boolean;
  /** Haptics for own actions (Telegram). */
  vibration: boolean;
}

const KEY = 'orb.settings';
export const DEFAULTS: SettingsValues = { quality: 'auto', breath: true, calm: false, vibration: true };

export class Settings {
  private v: SettingsValues;
  private readonly listeners = new Set<(v: SettingsValues) => void>();

  constructor(private readonly storage: Pick<Storage, 'getItem' | 'setItem'> | null = safeStorage()) {
    let stored: Partial<SettingsValues> = {};
    try { stored = JSON.parse(this.storage?.getItem(KEY) ?? '{}') ?? {}; } catch { /* broken JSON: defaults */ }
    this.v = { ...DEFAULTS };
    for (const k of Object.keys(DEFAULTS) as (keyof SettingsValues)[]) if (typeof stored[k] === typeof DEFAULTS[k]) (this.v as unknown as Record<string, unknown>)[k] = stored[k];
    if (!['auto', 'low', 'medium', 'high'].includes(this.v.quality)) this.v.quality = 'auto';
  }

  get values(): Readonly<SettingsValues> { return this.v; }
  get<K extends keyof SettingsValues>(k: K): SettingsValues[K] { return this.v[k]; }

  set<K extends keyof SettingsValues>(k: K, value: SettingsValues[K]) {
    if (this.v[k] === value) return;
    this.v = { ...this.v, [k]: value };
    try { this.storage?.setItem(KEY, JSON.stringify(this.v)); } catch { /* private mode: for this visit only */ }
    for (const fn of [...this.listeners]) fn(this.v);
  }

  /** Called on every change (and once now); returns the unsubscribe. */
  watch(fn: (v: SettingsValues) => void) { this.listeners.add(fn); fn(this.v); return () => { this.listeners.delete(fn); }; }

  /** The quality the scene is built with: `?q=` first (tuning), then the choice, else the device's own. */
  qualityOverride(param: string | null): string | null { return param ?? (this.v.quality === 'auto' ? null : this.v.quality); }
}

function safeStorage(): Storage | null { try { return localStorage; } catch { return null; } }

/** The system asks for reduced motion (the setting can only add to it). */
export const systemReducedMotion = () => typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
